/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo} from 'react'

import {getDisplayPrice} from '../utils/product-utils'
import {useD2BCustomer} from './use-d2b-customer'

/**
 * @typedef {object} ProductPricing
 * @property {string} currency
 * @property {number} price
 * @property {number} displayPrice
 * @property {boolean} hasDiscount
 * @property {boolean} isD2BEnabled
 */

/**
 * Gets the product pricing, allowing for extensibility using custom hooks
 * @param {object} product
 * @param {boolean} [isBonusProduct]
 * @return {ProductPricing}
 */
function useProductPricing(product, isBonusProduct = false) {
    const isD2BEnabled = useD2BCustomer().enabled
    return useMemo(() => {
        if (!product) return null
        const {price, displayPrice, hasDiscount, isFree} = getDisplayPrice(
            product,
            isBonusProduct,
            isD2BEnabled
        )
        return {
            currency: product.currency,
            price,
            displayPrice,
            hasDiscount,
            isFree
        }
    }, [product, isBonusProduct, isD2BEnabled])
}

export default useProductPricing
